import dayjs, { Dayjs } from "dayjs";
import { FilterOptions } from "../components/filter/FilterBar";
import { MenuItem } from "../config/nav-items.config";
import { PermissionClaim, PERMISSIONS } from "@/core/enums/permissions.enum";
import { FilterSearchPayload } from "../components/table/DataTable";

//Utility functions for home layout
export class GlobalUtils {
  //Search for parents and current navigation item, returns an array
  static getCurrentNavData(items: MenuItem[], selectedKeys: string[]): MenuItem[] {
    const result: MenuItem[] = [];
    let searchArray = items;
    for (const key of selectedKeys) {
      const item = searchArray.find(item => item.key.toString() === key.toString());
      if (item) {
        result.push(item);
        if (item.children) {
          searchArray = item.children;
        }
      }
    }
    return result;
  }

  static getPreviousMonthFilter() {
    let date = dayjs();
    if (date.date() < 20) {
      date = dayjs().add(-1, "month");
    }
    return {
      "year": {key: "year", value: date.year().toString()},
      "month": {key: "month", value: (date.month()+1).toString()}
    }
  }

  static downloadXLSX(blob: string, filename: string) {
    const url = window.URL.createObjectURL(new Blob([blob], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}.xlsx`);
    document.body.appendChild(link);
    link.click()
  }

  static downloadDOCX(blob: string, filename: string) {
    const url = window.URL.createObjectURL(new Blob([blob], {type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}.docx`);
    document.body.appendChild(link);
    link.click()
  }

  static divideArray<T>(array: T[], parts: number): T[][] {
    const result: T[][] = [];
    const chunkSize = Math.ceil(array.length / parts);
  
    for (let i = 0; i < parts; i++) {
      result.push(array.slice(i * chunkSize, (i + 1) * chunkSize));
    }
  
    return result;
  }

  static flattenArray(arr: any[]): any[] {
    return arr.reduce((flat, toFlatten) => {
        return flat.concat(Array.isArray(toFlatten) ? GlobalUtils.flattenArray(toFlatten) : toFlatten);
    }, []);
  }

  static findChanges<T>(before: T[], after: T[]): { added: T[], deleted: T[] } {
    const added = after.filter(item => !before.includes(item));
    const deleted = before.filter(item => !after.includes(item));

    return { added, deleted };
  }

  static arraysEqual<T>(array1: T[], array2: T[]): boolean {
    if (array1.length !== array2.length) {
        return false;
    }

    const sortedArray1 = [...array1].sort();
    const sortedArray2 = [...array2].sort();

    return sortedArray1.every((value, index) => value === sortedArray2[index]);
  }

  static arrayContains<T>(array1: T[], array2: T[]): boolean {
      return array2.every(value => array1.includes(value));
  }

  static parseArray(data?: string): string[] {
    console.log(data);
    if (data&&!data.includes(";")) {
      return [data];
    }
    return data?data.split(";").slice(0, -1):[];
  }

  static truncateText(text: string, wordLimit: number): string {
    // Split the text into an array of words
    const words = text.trim().split(/\s+/);
    
    // If the word count is less than or equal to the limit, return original text
    if (words.length <= wordLimit) {
        return text;
    }
    
    // Take the first 'wordLimit' words and join them back, adding ellipsis
    return words.slice(0, wordLimit).join(' ') + '...';
  }

  static checkPerm(claims: PermissionClaim[], permission?: string) {
    if (!permission) return true;
    function check(permission: string) {
      return (claim: PermissionClaim) => {
          if (claim.id === PERMISSIONS.ADMIN.key) return true;
          return claim.id === permission
      }
    }
    return claims.findIndex(check(permission))!=-1;
  }

  // In GlobalUtils
  static buildQueryParamsFromFilterMap(
    filterMap: Map<string, FilterSearchPayload>,
    additionalParams?: Record<string, any>
  ): string {
    const filterParams = Array.from(filterMap.entries())
      .filter(([_, v]) => v.value !== undefined && v.value !== null && v.value !== "")
      .map(([k, v]) => {
        if (Array.isArray(v.value)) {
          return v.value.map(val => `${encodeURIComponent(k)}=${encodeURIComponent(val)}`).join('&');
        }
        return `${encodeURIComponent(k)}=${encodeURIComponent(v.value)}`;
      });

    const extraParams = additionalParams
      ? Object.entries(additionalParams)
          .filter(([_, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => {
            if (Array.isArray(v)) {
              return v.map(val => `${encodeURIComponent(k)}=${encodeURIComponent(val)}`).join('&');
            }
            return `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          })
      : [];

    return [...filterParams, ...extraParams].join('&');
  }
}

export function deepCompare<T = any>(...args: T[]): boolean {
  let leftChain: any[];
  let rightChain: any[];

  function compare2Objects(x: any, y: any): boolean {
    let p: string;

    // remember that NaN === NaN returns false
    // and isNaN(undefined) returns true
    if (isNaN(x) && isNaN(y) && typeof x === 'number' && typeof y === 'number') {
      return true;
    }

    // Compare primitives and functions.     
    // Check if both arguments link to the same object.
    // Especially useful on the step where we compare prototypes
    if (x === y) {
      return true;
    }

    // Works in case when functions are created in constructor.
    // Comparing dates is a common scenario. Another built-ins?
    // We can even handle functions passed across iframes
    if ((typeof x === 'function' && typeof y === 'function') ||
      (x instanceof Date && y instanceof Date) ||
      (x instanceof RegExp && y instanceof RegExp) ||
      (x instanceof String && y instanceof String) ||
      (x instanceof Number && y instanceof Number)) {
      return x.toString() === y.toString();
    }

    // At last checking prototypes as good as we can
    if (!(x instanceof Object && y instanceof Object)) {
      return false;
    }

    if (x.isPrototypeOf(y) || y.isPrototypeOf(x)) {
      return false;
    }

    if (x.constructor !== y.constructor) {
      return false;
    }

    if (x.prototype !== y.prototype) {
      return false;
    }

    // Check for infinitive linking loops
    if (leftChain.indexOf(x) > -1 || rightChain.indexOf(y) > -1) {
      return false;
    }

    // Quick checking of one object being a subset of another.
    // todo: cache the structure of arguments[0] for performance
    for (p in y) {
      if (Object.prototype.hasOwnProperty.call(y, p) !== Object.prototype.hasOwnProperty.call(x, p)) {
        return false;
      } else if (typeof y[p] !== typeof x[p]) {
        return false;
      }
    }

    for (p in x) {
      if (Object.prototype.hasOwnProperty.call(y, p) !== Object.prototype.hasOwnProperty.call(x, p)) {
        return false;
      } else if (typeof y[p] !== typeof x[p]) {
        return false;
      }

      switch (typeof (x[p])) {
        case 'object':
        case 'function':
          leftChain.push(x);
          rightChain.push(y);

          if (!compare2Objects(x[p], y[p])) {
            return false;
          }

          leftChain.pop();
          rightChain.pop();
          break;

        default:
          if (x[p] !== y[p]) {
            return false;
          }
          break;
      }
    }

    return true;
  }

  if (args.length < 1) {
    return true; //Die silently? Don't know how to handle such case, please help...
    // throw "Need two or more arguments to compare";
  }

  for (let i = 1, l = args.length; i < l; i++) {
    leftChain = [];
    rightChain = [];
    if (!compare2Objects(args[0], args[i])) {
      return false;
    }
  }

  return true;
}

export function normalizeVietnameseString(str: string): string {
  str = str.normalize("NFD");
  str = str.replace(/[\u0300-\u036f]/g, "");
  str = str.replace(/đ/g, "d").replace(/Đ/g, "D");
  return str;
}

export function dateNoTZ(d?: string|Dayjs|Date): string|undefined {
    if (!d) return undefined;
    const date = dayjs(d);
    if (!date.isValid()) return "";
    const dateString = date.format();
    const dateNoTZ = dateString.split("+")[0] + "+07:00";
    return dateNoTZ;
}

export const DEF_MONTH_FORMAT = "MM/YYYY";
export const DEF_DATE_FORMAT = "DD/MM/YYYY";
export const DEF_DATETIME_FORMAT = "DD/MM/YYYY - HH:mm:ss";

